import { create } from "zustand";
import { WATCH_COLLECTION } from "../data/watchSeed.js";
import { canonicalizeActiveStraps } from "../data/strapAliases.js";
import { setCachedState } from "../services/localCache.js";
import { computeStrapHealth } from "../domain/strapLifecycle.js";

function buildInitialStraps() {
  const straps = {};
  for (const watch of WATCH_COLLECTION) {
    if (!watch.straps?.length) continue;
    for (const s of watch.straps) {
      straps[s.id] = { ...s, watchId: watch.id, thumbnail: null, photoUrl: null, wristShot: null, custom: false };
    }
  }
  return straps;
}

function buildInitialActive() {
  const active = {};
  for (const watch of WATCH_COLLECTION) {
    if (!watch.straps?.length) continue;
    active[watch.id] = watch.straps[0].id;
  }
  return active;
}

export const useStrapStore = create((set, get) => ({
  straps: buildInitialStraps(),
  activeStrap: buildInitialActive(),

  setActiveStrap: (watchId, strapId) =>
    set(s => ({ activeStrap: { ...s.activeStrap, [watchId]: strapId } })),

  addStrapPhoto: (strapId, thumbnail, photoUrl = null) =>
    set(s => ({
      straps: { ...s.straps, [strapId]: { ...s.straps[strapId], thumbnail, photoUrl: photoUrl ?? s.straps[strapId]?.photoUrl } },
    })),

  addWristShot: (strapId, wristShot) =>
    set(s => ({ straps: { ...s.straps, [strapId]: { ...s.straps[strapId], wristShot } } })),

  /** Add a fully custom strap */
  addStrap: (watchId, strapData) => {
    const id = `custom-${watchId}-${Date.now()}`;
    set(s => ({
      straps: { ...s.straps, [id]: { id, watchId, custom: true, thumbnail: null, photoUrl: null, wristShot: null, ...strapData } },
      activeStrap: { ...s.activeStrap, [watchId]: id },
    }));
    return id;
  },

  /** Edit label/color/type/useCase of any strap */
  updateStrap: (strapId, patch) =>
    set(s => ({ straps: { ...s.straps, [strapId]: { ...s.straps[strapId], ...patch } } })),

  /** Delete a strap (and reset active if it was active) */
  deleteStrap: (strapId) =>
    set(s => {
      const newStraps = { ...s.straps };
      const watchId = newStraps[strapId]?.watchId;
      delete newStraps[strapId];
      const newActive = { ...s.activeStrap };
      if (watchId && newActive[watchId] === strapId) {
        const fallback = Object.values(newStraps).find(x => x.watchId === watchId);
        newActive[watchId] = fallback?.id ?? null;
      }
      return { straps: newStraps, activeStrap: newActive };
    }),

  /** Move a strap from one watch to another (cross-strap) */
  moveStrap: (strapId, fromWatchId, toWatchId) =>
    set(s => {
      const strap = s.straps[strapId];
      if (!strap) return s;
      const newStraps = {
        ...s.straps,
        [strapId]: { ...strap, watchId: toWatchId, originalWatchId: strap.originalWatchId ?? fromWatchId, crossStrapped: true },
      };
      const newActive = { ...s.activeStrap };
      // Auto-activate on target watch
      newActive[toWatchId] = strapId;
      // If it was active on source, fall back to first remaining strap
      if (newActive[fromWatchId] === strapId) {
        const fallback = Object.values(newStraps).find(x => x.watchId === fromWatchId && x.id !== strapId);
        newActive[fromWatchId] = fallback?.id ?? null;
      }
      return { straps: newStraps, activeStrap: newActive };
    }),

  /** Return strap to its original watch */
  returnStrap: (strapId) =>
    set(s => {
      const strap = s.straps[strapId];
      if (!strap?.originalWatchId) return s;
      const origWatch = strap.originalWatchId;
      const curWatch = strap.watchId;
      const newStraps = {
        ...s.straps,
        [strapId]: { ...strap, watchId: origWatch, crossStrapped: false },
      };
      const newActive = { ...s.activeStrap };
      newActive[origWatch] = strapId;
      if (newActive[curWatch] === strapId) {
        const fallback = Object.values(newStraps).find(x => x.watchId === curWatch && x.id !== strapId);
        newActive[curWatch] = fallback?.id ?? null;
      }
      return { straps: newStraps, activeStrap: newActive };
    }),

  /** Get all cross-strapped items */
  getCrossStrapped: () => Object.values(get().straps).filter(s => s.crossStrapped),

  /** Increment wear count for a strap (called on outfit log) */
  incrementWearCount: (strapId) =>
    set(s => {
      const strap = s.straps[strapId];
      if (!strap) return s;
      return {
        straps: {
          ...s.straps,
          [strapId]: {
            ...strap,
            wearCount: (strap.wearCount ?? 0) + 1,
            lastWorn: new Date().toISOString().slice(0, 10),
          },
        },
      };
    }),

  /** Get strap stats — wear count, age estimation, replacement flag */
  getStrapStats: (strapId) => {
    const strap = get().straps[strapId];
    if (!strap) return null;
    const wears = strap.wearCount ?? 0;
    const type = (strap.type ?? "").toLowerCase();
    // Single source of truth for the lifespan/health model — shared with the strap
    // recommender + pill (was a divergent local lifespanMap). See domain/strapLifecycle.js.
    const { lifespan, healthPct, needsReplacement } = computeStrapHealth({ wears, label: strap.label, type: strap.type });
    return {
      wears,
      lastWorn: strap.lastWorn ?? null,
      type,
      healthPct,
      needsReplacement,
      lifespan,
    };
  },

  getActiveStrapObj: (watchId) => {
    const { straps, activeStrap } = get();
    const id = activeStrap[watchId];
    return id ? straps[id] : null;
  },

  getStrapsForWatch: (watchId) => Object.values(get().straps).filter(s => s.watchId === watchId),

  hydrate: (saved) => {
    if (!saved) return;
    // Normalize legacy strap IDs (e.g., rikka-titanium-bracelet → rikka-bracelet)
    // so user IDB caches that pre-date the v1.13.40 rename resolve correctly.
    const canonicalActive = canonicalizeActiveStraps(saved.activeStrap);
    set(s => ({
      straps: { ...s.straps, ...saved.straps },
      activeStrap: { ...s.activeStrap, ...canonicalActive },
    }));
  },

  serialise: () => {
    const { straps, activeStrap } = get();
    return { straps, activeStrap };
  },
}));

function persist() {
  const state = useStrapStore.getState();
  const strapsToSave = {};
  for (const [id, s] of Object.entries(state.straps)) {
    strapsToSave[id] = { id: s.id, watchId: s.watchId, label: s.label, color: s.color, type: s.type, useCase: s.useCase,
      thumbnail: s.thumbnail ?? null, photoUrl: s.photoUrl ?? null, wristShot: s.wristShot ?? null, custom: s.custom ?? false,
      wearCount: s.wearCount ?? 0, lastWorn: s.lastWorn ?? null,
      originalWatchId: s.originalWatchId ?? null, crossStrapped: s.crossStrapped ?? false };
  }
  setCachedState({ strapStore: { straps: strapsToSave, activeStrap: state.activeStrap } }).catch(() => {});
}
useStrapStore.subscribe(() => persist());

// ── Stable selectors — prevent re-render storms in WatchDashboard ───────────
// These return the same reference when the underlying data hasn't changed.

/**
 * Stable selector: returns the active strap object for a watch.
 * Usage: useStrapStore(selectActiveStrap(watchId))
 */
export function selectActiveStrap(watchId) {
  let prev = null;
  return (state) => {
    const id = state.activeStrap[watchId];
    const strap = id ? state.straps[id] : null;
    // Referential equality: return same object if strap hasn't changed
    if (prev && strap && prev.id === strap.id && prev.wearCount === strap.wearCount
        && prev.label === strap.label && prev.color === strap.color
        && prev.thumbnail === strap.thumbnail && prev.lastWorn === strap.lastWorn) {
      return prev;
    }
    prev = strap;
    return strap;
  };
}

/**
 * Stable selector: returns straps array for a watch.
 * Usage: useStrapStore(selectStrapsForWatch(watchId))
 */
export function selectStrapsForWatch(watchId) {
  let prev = [];
  return (state) => {
    const list = Object.values(state.straps).filter(s => s.watchId === watchId);
    if (list.length === prev.length && list.every((s, i) => s.id === prev[i]?.id)) return prev;
    prev = list;
    return list;
  };
}
